import React, { useState } from 'react';
import { Award, Edit, Eye, KeyRound, Trash2, UserPlus } from 'lucide-react';
import { Card } from '../../ui/Card';
import { Badge } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { Modal } from '../../ui/Modal';
import { Tooltip } from '../../ui/Tooltip';
import { PermissionGate } from '../../PermissionGate';
import { EnrollmentFormModal } from './EnrollmentFormModal';
import { EnrollmentDetailModal } from './EnrollmentDetailModal';
import { trainingCoursesStore } from '../../../services/trainingCoursesStore';
import { formatCourseDate } from '../../../utils/trainingCourses';
import { OnDemandEnrollment, TrainingCourse } from '../../../types/trainingCourse';
interface OnDemandParticipantsCardProps {
  course: TrainingCourse;
}

// On-demand courses: list of the single participants who bought the course
export function OnDemandParticipantsCard({
  course
}: OnDemandParticipantsCardProps) {
  const enrollments = [...(course.enrollments || [])].sort((a, b) => b.enrolledAt.localeCompare(a.enrolledAt));
  const [isAdding, setIsAdding] = useState(false);
  const [editing, setEditing] = useState<OnDemandEnrollment | null>(null);
  const [viewing, setViewing] = useState<OnDemandEnrollment | null>(null);
  const [deleting, setDeleting] = useState<OnDemandEnrollment | null>(null);
  // Default purchase price for new participants: the last one used on the course
  const lastPrice = enrollments.find(e => e.price !== undefined)?.price;
  const save = (e: OnDemandEnrollment) => trainingCoursesStore.saveEnrollment(course.id, e);
  const stamp = () => new Date().toISOString();
  return <Card noHoverShadow noPadding data-id="element-1317" data-mp="3k5cs6xobsnem">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 p-6 pb-4" data-id="element-1318" data-mp="3k6ec7rnbkk8h">
        <div data-id="element-1319" data-mp="3g5can45xjw3c">
          <h3 className="text-lg font-semibold text-seform-navy" data-id="element-1320" data-mp="1id845x9y6a96">
            Partecipanti{' '}
            <span className="text-slate-500 font-normal" data-id="element-1321" data-mp="1ebmrabxexvyz">· {enrollments.length}</span>
          </h3>
          <p className="text-sm text-slate-500 mt-0.5" data-id="element-1322" data-mp="37ubvr3vvh146">
            Persone che hanno acquistato il corso.
          </p>
        </div>
        <PermissionGate module="training" action="edit" data-id="element-1323" data-mp="3cm3qlqm29t04">
          <Button size="sm" startContent={<UserPlus className="h-4 w-4" data-id="element-1325" data-mp="2yuyqknes6yp9" />} onClick={() => setIsAdding(true)} data-id="element-1324" data-mp="2ytnkui1rzgvi">
            Aggiungi partecipante
          </Button>
        </PermissionGate>
      </div>

      {enrollments.length === 0 ? <p className="px-6 pb-6 text-sm text-slate-500" data-id="element-1326" data-mp="3qiqru5l1r5bu">
          Nessun partecipante iscritto a questo corso.
        </p> : <div className="overflow-x-auto border-t border-slate-100" data-id="element-1327" data-mp="3hkoin2r5t6dh">
          <table className="w-full text-left border-collapse" data-id="element-1328" data-mp="3hmhncci7pgiu">
            <thead data-id="element-1329" data-mp="3mhon598d212h">
              <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-1330" data-mp="3mlcj0f1a2zkd">
                <th className="px-4 py-3 pl-6 whitespace-nowrap" data-id="element-1331" data-mp="2b3t59sq2ne7k">Nome</th>
                <th className="px-4 py-3 whitespace-nowrap" data-id="element-1332" data-mp="2b1q9es28mqux">Iscrizione</th>
                <th className="px-4 py-3 whitespace-nowrap" data-id="element-1333" data-mp="22lhkh6x99r26">Credenziali</th>
                <th className="px-4 py-3 whitespace-nowrap" data-id="element-1334" data-mp="1ykfiiibyf0wi">Attestato</th>
                <th className="px-4 py-3 whitespace-nowrap" data-id="element-1335" data-mp="2clrw4n3n6qf8">Invio credenziali</th>
                <th className="px-4 py-3 whitespace-nowrap" data-id="element-1336" data-mp="27m5hqac2v44u">Invio attestato</th>
                <th className="px-4 py-3 pr-6 whitespace-nowrap text-right" data-id="element-1337" data-mp="27k2hyyl4hwmx">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-1338" data-mp="1exrk3vowi9tt">
              {enrollments.map(e => <tr key={e.id} className="hover:bg-slate-50/80 transition-colors" data-id="element-1339" data-mp="1tm474360gic6">
                  <td className="px-4 py-3 pl-6 min-w-[12rem]" data-id="element-1340" data-mp="1to797lkfu1x9">
                    <p className="text-sm font-medium text-slate-900" data-id="element-1341" data-mp="1ou2ziujsffsh">
                      {e.firstName} {e.lastName}
                    </p>
                    <p className="text-xs text-slate-500" data-id="element-1342" data-mp="w705ofq23t8k">{e.email}</p>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600 tabular-nums whitespace-nowrap" data-id="element-1343" data-mp="3vkzj65ezjfi6">
                    {formatCourseDate(e.enrolledAt)}
                  </td>
                  <td className="px-4 py-3" data-id="element-1344" data-mp="4ey65rbgmbtw">
                    <Badge variant={e.credentialsSentAt ? 'success' : 'warning'} size="sm" data-id="element-1345" data-mp="8frps8hvd4gz">
                      {e.credentialsSentAt ? 'Inviate' : 'Da inviare'}
                    </Badge>
                  </td>
                  <td className="px-4 py-3" data-id="element-1346" data-mp="i6w89i6r77gk">
                    <Badge variant={e.certificateSentAt ? 'success' : 'warning'} size="sm" data-id="element-1347" data-mp="i87856x2vv2e">
                      {e.certificateSentAt ? 'Inviato' : 'Da inviare'}
                    </Badge>
                  </td>
                  <td className="px-4 py-3" data-id="element-1348" data-mp="w5vu6i2vt891">
                    <Button variant="flat" color="secondary" size="sm" className="whitespace-nowrap" startContent={<KeyRound className="h-3.5 w-3.5" data-id="element-1350" data-mp="2xy0h1f5ixfcw" />} onClick={() => save({
                ...e,
                credentialsSentAt: stamp()
              })} data-id="element-1349" data-mp="w4uiqchyejqs">
                      {e.credentialsSentAt ? 'Reinvia credenziali' : 'Invia credenziali'}
                    </Button>
                  </td>
                  <td className="px-4 py-3" data-id="element-1351" data-mp="2gopxly1i1yrx">
                    <Tooltip content={e.completed ? e.certificateSentAt ? 'Invia di nuovo l’attestato' : 'Invia l’attestato al partecipante' : 'Disponibile quando il corso risulta completato'} data-id="element-1352" data-mp="2cmbdlam3gsz2">
                      <Button variant="flat" color="secondary" size="sm" className="whitespace-nowrap" startContent={<Award className="h-3.5 w-3.5" data-id="element-1354" data-mp="1jxxeerj9rqlx" />} disabled={!e.completed} onClick={() => save({
                  ...e,
                  certificateSentAt: stamp()
                })} data-id="element-1353" data-mp="30dqklgotfygn">
                        {e.certificateSentAt ? 'Reinvia attestato' : 'Invia attestato'}
                      </Button>
                    </Tooltip>
                  </td>
                  <td className="px-4 py-3 pr-6" data-id="element-1355" data-mp="1cghaia9n463a">
                    <div className="flex items-center justify-end gap-1" data-id="element-1356" data-mp="1geovjhb4ztka">
                      <Tooltip content="Visualizza partecipante" data-id="element-1357" data-mp="12nthzrpp0oad">
                        <Button variant="light" size="sm" className="!px-1" aria-label="Visualizza partecipante" onClick={() => setViewing(e)} data-id="element-1358" data-mp="12mid1okihaho">
                          <Eye className="h-4 w-4 text-slate-500" data-id="element-1359" data-mp="xmvzfe0rts8c" />
                        </Button>
                      </Tooltip>
                      <PermissionGate module="training" action="edit" data-id="element-1360" data-mp="op24a6eqt4mr">
                        <Tooltip content="Modifica partecipante" data-id="element-1361" data-mp="oqd8u8g0igex">
                          <Button variant="light" size="sm" className="!px-1" aria-label="Modifica partecipante" onClick={() => setEditing(e)} data-id="element-1362" data-mp="53ac7jlp6dpa">
                            <Edit className="h-4 w-4 text-seform-orange" data-id="element-1363" data-mp="a0k2t2ktovep" />
                          </Button>
                        </Tooltip>
                      </PermissionGate>
                      <PermissionGate module="training" action="delete" data-id="element-1364" data-mp="j1qg72s6dq3q">
                        <Tooltip content="Elimina partecipante" data-id="element-1365" data-mp="xrpgbb3ajblu">
                          <Button variant="light" size="sm" className="!px-1" aria-label="Elimina partecipante" onClick={() => setDeleting(e)} data-id="element-1366" data-mp="ssctgqcf64j9">
                            <Trash2 className="h-4 w-4 text-red-500" data-id="element-1367" data-mp="295qljld30pox" />
                          </Button>
                        </Tooltip>
                      </PermissionGate>
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>}

      <EnrollmentFormModal isOpen={isAdding || Boolean(editing)} enrollment={editing || undefined} defaultPrice={lastPrice} onClose={() => {
      setIsAdding(false);
      setEditing(null);
    }} onSave={e => {
      save(e);
      setIsAdding(false);
      setEditing(null);
    }} data-id="element-1368" data-mp="2n5hwfd8axpyp" />
      <EnrollmentDetailModal enrollment={viewing} onClose={() => setViewing(null)} data-id="element-1369" data-mp="1zdubrd1mvb4v" />
      <Modal isOpen={Boolean(deleting)} onClose={() => setDeleting(null)} title="Elimina partecipante" size="md" data-id="element-1370" data-mp="247ot49trne0s">
        <p className="text-sm text-slate-600" data-id="element-1371" data-mp="1bpb3uymhx0db">
          Vuoi eliminare{' '}
          <span className="font-semibold text-slate-900" data-id="element-1372" data-mp="146bl8v6uzcv0">
            {deleting?.firstName} {deleting?.lastName}
          </span>{' '}
          dai partecipanti del corso? L’operazione non si può annullare.
        </p>
        <div className="flex justify-end gap-3 mt-6" data-id="element-1373" data-mp="u7mdvwcl9at0">
          <Button variant="flat" color="secondary" onClick={() => setDeleting(null)} data-id="element-1374" data-mp="uagxjlns2rld">
            Annulla
          </Button>
          <Button color="danger" onClick={() => {
          if (deleting) trainingCoursesStore.removeEnrollment(course.id, deleting.id);
          setDeleting(null);
        }} data-id="element-1375" data-mp="l7fsloanf6ja">
            Elimina
          </Button>
        </div>
      </Modal>
    </Card>;
}